import { inject, Injectable } from '@angular/core';
import { BaseService } from 'src/app/shared/services/base.service';
import { GET_OFFERS, USER_ACCOUNT } from '../../shared/config/app.config';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { CustomProfileAndPreferencesService } from 'src/app/custom-profile-and-preferences/custom-profile-and-preferences.service';
import {
  concatMap,
  EMPTY,
  filter,
  map,
  Observable,
  tap,
  combineLatest,
  interval,
  take,
} from 'rxjs';
import {
  Offer,
  OfferPerformanceData,
  OfferList,
} from '../models/offers.model';
import {
  OffersAcceptanceResponse,
  OffersPayload,
  OffersUpgradePayload,
} from '../models/offers-form.model';

@Injectable({
  providedIn: 'root',
})
export class MyOffersService {
  private readonly baseService = inject(BaseService);
  private readonly userAccountFacade = inject(UserAccountFacade);
  private readonly customProfileAndPreferencesService = inject(
    CustomProfileAndPreferencesService
  );

  readonly b2bUser$ = this.customProfileAndPreferencesService._userAccountData;
  readonly user$ = this.userAccountFacade.get();

  /**
   * Always fetch fresh offers after both user ID and selected location are available.
   */
  fetchFreshOffers(): Observable<OfferList> {
    return combineLatest([
      this.userAccountFacade.get().pipe(filter((user) => !!user?.uid)),
      this.getSelectedLocationWhenReady(),
    ]).pipe(
      concatMap(([user, location]) => {
        const accountNumber = location.accountNumber;
        return this.baseService
          .get(`/users/${user.uid}/${GET_OFFERS}/${accountNumber}`)
          .pipe(
            tap((res) =>
              console.log('[MyOffersService] Raw OCC response:', res)
            ),
            map((res: any) => {
              return {
                offers: res?.offers || [],
                offerPerformanceData: res?.offerPerformanceData || null,
              } as OfferList;
            })
          );
      })
    );
  }

  /**
   * Continuously checks for selected location in sessionStorage until available.
   */
  public getSelectedLocationWhenReady(): Observable<{ accountNumber: string }> {
    return interval(200).pipe(
      map(() => {
        const locations = JSON.parse(
          sessionStorage.getItem('selectedLocations')
        );
        return locations?.[0] || null;
      }),
      filter((location) => !!location?.accountNumber),
      take(1)
    );
  }

  confirmOffer(offerPayload: OffersPayload): Observable<OffersAcceptanceResponse> {
    return this.userAccountFacade.get().pipe(
      concatMap((user) => {
        const location = JSON.parse(sessionStorage.getItem('selectedLocations'));
        if (!location?.length) return EMPTY;

        const id = location[0].accountNumber;
        const apiEndpoint = `${USER_ACCOUNT}${user.uid}/offers/group/loader/${id}`;
        return this.baseService.post(
          apiEndpoint,
          offerPayload
        ) as Observable<OffersAcceptanceResponse>;
      })
    );
  }

  upgradeOffer(
    offerId: string,
    payload: OffersUpgradePayload
  ): Observable<OffersAcceptanceResponse> {
    return this.userAccountFacade.get().pipe(
      concatMap((user) => {
        const location = JSON.parse(sessionStorage.getItem('selectedLocations'));
        if (!location?.length) return EMPTY;

        const b2bUnitId = location[0].accountNumber;
        const apiEndpoint = `${USER_ACCOUNT}${user.uid}/offers/${b2bUnitId}/${offerId}/upgrade`;

        return this.baseService.post(
          apiEndpoint,
          payload
        ) as Observable<OffersAcceptanceResponse>;
      })
    );
  }

  getOfferById(offerId: string): Observable<Offer> {
    return combineLatest([
      this.userAccountFacade.get().pipe(filter((user) => !!user?.uid)),
      this.getSelectedLocationWhenReady(),
    ]).pipe(
      take(1),
      concatMap(([user, location]) => {
        console.log('[MyOffersService] user:', user);
        console.log('[MyOffersService] location from combineLatest:', location);

        const b2bUnitId = location.accountNumber;
        const apiEndpoint = `${USER_ACCOUNT}${user.uid}/offers/${b2bUnitId}/${offerId}`;

        console.log('[MyOffersService] Fetching single offer from:', apiEndpoint);

        // Use baseService.get() - backend now returns 200 with null body for active but ineligible offers
        // or 404 for not found/deactivated offers, so we don't need complex error reconstruction
        return this.baseService.get(apiEndpoint, { lang: 'en', curr: 'USD' }).pipe(
          tap((res) =>
            console.log('[MyOffersService] Raw single offer response:', res)
          ),
          map((res: any) => {
            // Backend always returns 200 with OfferWsDto (never null) for all cases:
            // - Active and eligible: active=true, isEligible=true
            // - Active but ineligible: active=true, isEligible=false
            // - Inactive: active=false, isEligible=false
            // - Not found: 404 (should propagate to error callback, but handle empty array defensively)
            if (Array.isArray(res) && res.length === 0) {
              // Defensive check: if baseService swallows 404 and returns empty array, 
              // this shouldn't happen but handle it gracefully
              console.warn('[MyOffersService] Received empty array - unexpected, should be OfferWsDto or 404');
              return null as unknown as Offer;
            }
            // Return the offer data with isEligible and active flags
            return res as Offer;
          })
        );
      })
    );
  }
}
